/**
 * iOS 優化的音訊播放工具
 * 用於處理所有音訊播放，自動優化 iOS 設備的播放體驗
 */

// 檢測是否為 iOS 設備
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent.toLowerCase()
  return /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * iOS 優化的音訊播放函數
 * 自動檢測設備類型並應用相應的優化策略
 * 
 * @param audioUrl 音訊 URL（可以是 Blob URL 或 HTTP URL）
 * @param options 播放選項
 * @returns Promise<void>
 */
export const playAudioIOSOptimized = async (
  audioUrl: string,
  options: {
    onStart?: () => void
    onEnd?: () => void
    onError?: (error: Error) => void
  } = {}
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio(audioUrl)
    
    // iOS 優化：設置預加載
    audio.preload = 'auto'
    
    let timeout: NodeJS.Timeout | null = null
    let isResolved = false
    
    const cleanup = () => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      // 清理 Blob URL（延遲清理，確保播放完成）
      if (audioUrl.startsWith('blob:')) {
        setTimeout(() => URL.revokeObjectURL(audioUrl), 1000)
      }
    }
    
    const handleEnded = () => {
      if (!isResolved) {
        isResolved = true
        options.onEnd?.()
        cleanup()
        resolve()
      }
    }
    
    const handleError = (error: Event) => {
      if (!isResolved) {
        isResolved = true
        const err = new Error('音訊播放失敗')
        options.onError?.(err)
        cleanup()
        reject(err)
      }
    }
    
    // iOS 優化：等待音訊加載完成
    const handleCanPlayThrough = () => {
      if (isResolved) return
      
      // 清除超時
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      
      // 檢查是否使用緩存（readyState 和 networkState）
      if (process.env.NODE_ENV === 'development') {
        const networkState = audio.networkState
        const readyState = audio.readyState
        const cacheStatus = networkState === HTMLMediaElement.NETWORK_IDLE 
          ? '可能使用緩存' 
          : networkState === HTMLMediaElement.NETWORK_LOADING 
          ? '正在載入' 
          : '未知狀態'
        // console.log(`[播放] 音檔加載完成 (networkState: ${networkState}, readyState: ${readyState}, ${cacheStatus}): ${audioUrl.substring(audioUrl.lastIndexOf('/') + 1)}`)
      }
      
      // 觸發開始回調
      options.onStart?.()
      
      // 嘗試播放
      audio.play()
        .then(() => {
          // 播放成功，等待播放完成
        })
        .catch((playError) => {
          // iOS 可能需要用戶交互，嘗試恢復 AudioContext
          if (isIOS()) {
            // 恢復 AudioContext（如果存在）
            try {
              const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext
              if (AudioContextClass) {
                // 嘗試恢復所有暫停的 AudioContext
                const contexts = (window as any).__audioContexts || []
                Promise.all(contexts.map((ctx: AudioContext) => 
                  ctx.state === 'suspended' ? ctx.resume() : Promise.resolve()
                )).then(() => {
                  // 重試播放
                  audio.play().catch((retryError) => {
                    if (!isResolved) {
                      isResolved = true
                      cleanup()
                      reject(retryError)
                    }
                  })
                }).catch(() => {
                  // 即使恢復失敗也嘗試播放
                  audio.play().catch((retryError) => {
                    if (!isResolved) {
                      isResolved = true
                      cleanup()
                      reject(retryError)
                    }
                  })
                })
              } else {
                // 沒有 AudioContext，直接重試
                audio.play().catch((retryError) => {
                  if (!isResolved) {
                    isResolved = true
                    cleanup()
                    reject(retryError)
                  }
                })
              }
            } catch (e) {
              // 忽略錯誤，直接重試播放
              audio.play().catch((retryError) => {
                if (!isResolved) {
                  isResolved = true
                  cleanup()
                  reject(retryError)
                }
              })
            }
          } else {
            // 非 iOS 設備，直接拒絕
            if (!isResolved) {
              isResolved = true
              cleanup()
              reject(playError)
            }
          }
        })
    }
    
    // 設置事件監聽器
    audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true })
    audio.addEventListener('ended', handleEnded, { once: true })
    audio.addEventListener('error', handleError, { once: true })
    
    // iOS 優化：設置超時（避免無限等待）
    timeout = setTimeout(() => {
      if (isResolved) return
      
      // 檢查音訊狀態
      if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        // 如果已經有足夠的數據，直接播放
        options.onStart?.()
        audio.play()
          .then(() => {
            // 播放成功
          })
          .catch((playError) => {
            if (!isResolved) {
              isResolved = true
              cleanup()
              reject(playError)
            }
          })
      } else {
        // 超時且數據不足
        if (!isResolved) {
          isResolved = true
          cleanup()
          reject(new Error('音訊加載超時'))
        }
      }
    }, isIOS() ? 8000 : 5000) // iOS 給更長的超時時間（8秒）
    
    // 開始加載音訊
    audio.load()
    
    // iOS 優化：如果音訊已經可以播放，立即觸發
    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      handleCanPlayThrough()
    }
  })
}

